import React, { useState } from 'react';
import { CheckCircle2, Clipboard, RotateCcw } from 'lucide-react';
import { PastePopup } from './PastePopup';
import type { FrequencyTemperatureRow } from './utils/buildFrequencyTemperatureRows';

interface FrequencyTemperatureTableProps {
  icon: React.ReactNode;
  title: string;
  subtitle: string;
  headerLabel: string;
  headerAccent: string;
  rows: FrequencyTemperatureRow[];
  individualCalibrationData: any;
  setIndividualCalibrationData?: React.Dispatch<React.SetStateAction<any>>;
  onClearFields?: () => void;
  /** Preview mode: renders each row's value as plain text, hides Paste Data/Clear. */
  readOnly?: boolean;
}

/**
 * One editable table for Step 2 (Frequency Data / Temperature Data), or the
 * same table rendered read-only for Step 3's Preview & Confirm — same rows,
 * same layout, so the preview can never drift out of sync with what Step 2
 * actually collects. The row list scrolls within a fixed-height body instead
 * of pushing the page down for sensors with many calibration points.
 */
export const FrequencyTemperatureTable: React.FC<FrequencyTemperatureTableProps> = ({
  icon,
  title,
  subtitle,
  headerLabel,
  headerAccent,
  rows,
  individualCalibrationData,
  setIndividualCalibrationData,
  onClearFields,
  readOnly = false,
}) => {
  const [showPastePopup, setShowPastePopup] = useState(false);

  // Parse the popup's applied text directly against this table's own rows —
  // each line is "label<TAB>value" in the same order as `rows`, so this is a
  // straight positional match with no ambiguity.
  const applyPastedText = (text: string) => {
    if (!setIndividualCalibrationData) return;
    const lines = text.split(/\r?\n/);
    setIndividualCalibrationData((prev: any) => {
      const updates: Record<string, string> = {};
      rows.forEach((row, idx) => {
        const line = lines[idx] ?? '';
        const value = line.split('\t')[1]?.trim() ?? '';
        if (value !== '') {
          updates[row.key] = value;
        }
      });
      return { ...prev, ...updates };
    });
  };

  const filledCount = rows.filter((row) => {
    const value = individualCalibrationData[row.key];
    return value !== undefined && value !== null && String(value).trim() !== '';
  }).length;

  return (
    <div className="rounded-lg border border-blue-100 bg-white">
      <div className="flex items-center justify-between border-b border-gray-100 p-4">
        <div className="flex items-center gap-2.5">
          {icon}
          <div>
            <h4 className="text-sm font-semibold text-blue-800">{title}</h4>
            <p className="text-xs text-gray-500">{subtitle}</p>
          </div>
        </div>
        {!readOnly && (
          <button
            onClick={() => setShowPastePopup(true)}
            className="btn-secondary flex items-center text-sm"
          >
            <Clipboard className="mr-1.5 h-3.5 w-3.5" />
            Paste Data
          </button>
        )}
      </div>

      <div className="max-h-[360px] overflow-y-auto">
        <table className="w-full">
          <thead className="sticky top-0 z-10">
            <tr className={headerAccent}>
              <th className="w-10 px-4 py-2 text-left text-xs font-semibold text-gray-700">#</th>
              <th className="px-4 py-2 text-left text-xs font-semibold text-gray-700">{headerLabel}</th>
              <th className="px-4 py-2 text-left text-xs font-semibold text-gray-700">Value</th>
              {!readOnly && <th className="w-10 px-2 py-2" />}
            </tr>
          </thead>
          <tbody>
            {rows.map((row, index) => {
              const value = individualCalibrationData[row.key];
              const isFilled = value !== undefined && value !== null && String(value).trim() !== '';
              return (
                <tr key={row.key} className="border-t border-gray-100">
                  <td className="px-4 py-2 text-sm text-gray-400">{index + 1}</td>
                  <td className="px-4 py-2 text-sm text-gray-700">{row.label}</td>
                  <td className="px-4 py-2">
                    {readOnly ? (
                      <span className={`text-sm font-medium ${isFilled ? 'text-gray-900' : 'text-gray-400'}`}>
                        {isFilled ? value : '—'}
                      </span>
                    ) : (
                      <input
                        type="number"
                        step="0.01"
                        className="input w-full"
                        placeholder={row.placeholder}
                        value={value ?? ''}
                        onChange={(e) =>
                          setIndividualCalibrationData?.((prev: any) => ({
                            ...prev,
                            [row.key]: e.target.value,
                          }))
                        }
                      />
                    )}
                  </td>
                  {!readOnly && (
                    <td className="px-2 py-2 text-center">
                      {isFilled && <CheckCircle2 className="h-4 w-4 text-green-500" />}
                    </td>
                  )}
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {!readOnly && (
        <div className="flex items-center justify-between border-t border-gray-100 px-4 py-2.5">
          <div className="flex items-center gap-2">
            <div className="h-2 w-2 rounded-full bg-green-400" />
            <span className="text-xs font-medium text-gray-600">
              {filledCount} of {rows.length} values filled
            </span>
          </div>
          <button
            onClick={onClearFields}
            className="flex items-center gap-1 text-xs font-medium text-gray-400 transition-colors hover:text-red-600"
          >
            <RotateCcw className="h-3 w-3" />
            Clear
          </button>
        </div>
      )}

      {showPastePopup && (
        <PastePopup
          title={`Paste ${headerLabel} Data`}
          templates={rows.map((row) => `${row.label}\t`)}
          onClose={() => setShowPastePopup(false)}
          onApply={applyPastedText}
        />
      )}
    </div>
  );
};
